import { ArrowLeft, Loader2, Plus, Save, Upload, X } from "lucide-react";
import { useState } from "react";
import { useToast } from "../ui/Toast";
import api from "../../services/api";

const GRADES = ["A+", "A", "A-", "B+", "B", "C"];

const PlaybookEditor = ({ initialData, onBack, onSaveSuccess }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    title: initialData?.title || "",
    setupType: initialData?.setupType || "",
    grade: initialData?.grade || "A",
    description: initialData?.description || "",
    indicators: initialData?.indicators || [],
    rules: initialData?.rules || [],
  });

  const [screenshot, setScreenshot] = useState(null); // File object
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // New item inputs
  const [indicatorInput, setIndicatorInput] = useState("");
  const [ruleInput, setRuleInput] = useState("");

  const handleAddItem = (field, value, setter) => {
    if (!value.trim()) return;
    setFormData(prev => ({ ...prev, [field]: [...prev[field], value.trim()] }));
    setter("");
  };

  const handleRemoveItem = (field, index) => {
    setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
        const payload = { ...formData };
        
        // Handle Screenshot if new file selected (mock upload logic for now or implement real one if route exists)
        // We have /api/upload. Let's use it.
        if (screenshot) {
             const fd = new FormData();
             fd.append("image", screenshot);
             const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
             if (data.success) {
                  // Append to screenshots array (payload schema)
                  // For MVP let's assume one main screenshot or append to existing list
                  payload.screenshots = [...(initialData?.screenshots || []), { url: data.data.url, type: 'image' }];
             }
        }

        if (initialData?.id) {
            await api.put(`/playbooks/${initialData.id}`, payload);
        } else {
            await api.post("/playbooks", payload);
        }
        onSaveSuccess();
    } catch (error) {
        console.error("Save Playbook Failed", error);
        toast.error("Failed to save. Title unique?");
    } finally {
        setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="bg-slate-800 rounded-xl border border-slate-700 p-6 space-y-6">
       
       {/* Actions Bar */}
       <div className="flex justify-between items-center pb-4 border-b border-slate-700">
           <button type="button" onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2">
               <ArrowLeft size={18} /> Back
           </button>
           <button 
             type="submit" 
             disabled={saving}
             className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 shadow-lg"
           >
              {saving ? <Loader2 className="animate-spin" /> : <Save size={18} />}
              Save Play
           </button>
       </div>

       {/* Main Info */}
       <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           <div className="md:col-span-2 space-y-4">
               <div>
                   <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Play Title</label>
                   <input 
                     required
                     value={formData.title}
                     onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                     className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white text-lg font-bold focus:border-indigo-500 outline-none"
                     placeholder="e.g. Bull Flag Breakout"
                   />
               </div>
               <div>
                   <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Context / Setup Type</label>
                   <input 
                     value={formData.setupType}
                     onChange={(e) => setFormData(p => ({ ...p, setupType: e.target.value }))}
                     className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white focus:border-indigo-500 outline-none"
                     placeholder="e.g. Trend Continuation"
                   />
               </div>
               <div>
                   <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Description & Theory</label>
                   <textarea 
                     value={formData.description}
                     onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                     rows="8"
                     className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white focus:border-indigo-500 outline-none resize-y"
                     placeholder="Describe the logic, key levels, and psychology..."
                   />
               </div>
           </div>

           {/* Sidebar: Grade & Meta */}
           <div className="space-y-6">
               <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                   <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Grade Quality</label>
                   <div className="grid grid-cols-3 gap-2">
                       {GRADES.map(g => (
                           <button
                             type="button"
                             key={g}
                             onClick={() => setFormData(p => ({ ...p, grade: g }))}
                             className={`py-2 rounded font-bold transition-all ${formData.grade === g ? "bg-indigo-600 text-white shadow" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
                           >
                               {g}
                           </button>
                       ))}
                   </div>
               </div>

               {/* Indicators Tag Input */}
               <div>
                   <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Indicators</label>
                   <div className="flex gap-2 mb-2">
                       <input 
                         value={indicatorInput}
                         onChange={(e) => setIndicatorInput(e.target.value)}
                         onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddItem("indicators", indicatorInput, setIndicatorInput))}
                         className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white"
                         placeholder="Add (e.g. VWAP)"
                       />
                       <button type="button" onClick={() => handleAddItem("indicators", indicatorInput, setIndicatorInput)} className="bg-slate-700 px-3 rounded text-white"><Plus size={16} /></button>
                   </div>
                   <div className="flex flex-wrap gap-2">
                       {formData.indicators.map((tag, i) => (
                           <span key={i} className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-2 py-1 rounded text-xs flex items-center gap-1">
                               {tag}
                               <button type="button" onClick={() => handleRemoveItem("indicators", i)}><X size={12} /></button>
                           </span>
                       ))}
                   </div>
               </div>

               {/* Rules List */}
               <div>
                   <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Key Rules</label>
                   <div className="flex gap-2 mb-2">
                        <input 
                         value={ruleInput}
                         onChange={(e) => setRuleInput(e.target.value)}
                         onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddItem("rules", ruleInput, setRuleInput))}
                         className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white"
                         placeholder="Add rule..."
                       />
                       <button type="button" onClick={() => handleAddItem("rules", ruleInput, setRuleInput)} className="bg-slate-700 px-3 rounded text-white"><Plus size={16} /></button>
                   </div>
                   <ul className="space-y-1">
                        {formData.rules.map((rule, i) => (
                           <li key={i} className="text-xs text-slate-300 bg-slate-900 p-2 rounded flex justify-between items-center group">
                               <span>• {rule}</span>
                               <button type="button" onClick={() => handleRemoveItem("rules", i)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400"><X size={12} /></button>
                           </li>
                        ))}
                   </ul>
               </div>
               
               {/* Screenshot Upload */}
               <div>
                   <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Example Screenshot</label>
                   <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center hover:border-indigo-500/50 transition-colors">
                       <input 
                          type="file" 
                          id="playbook-screenshot" 
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => setScreenshot(e.target.files[0])}
                       />
                       <label htmlFor="playbook-screenshot" className="cursor-pointer flex flex-col items-center gap-2">
                           <Upload className="text-slate-500" />
                           <span className="text-xs text-slate-400">{screenshot ? screenshot.name : "Click to Upload Chart"}</span>
                       </label>
                   </div>
               </div>

           </div>
       </div>

    </form>
  );
};

export default PlaybookEditor;
